import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { GroupVisitSession, GroupVisitSessionControllerApi } from "@/services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  getXtenantId,
  useBaseContent,
} from "@/utils/utils";

export const GROUP_SESSION_LIST_QUERY_KEY = "group-session-list";

type Params = {
  pagination: DGT_Pagination;
  fromDate?: string;
  toDate?: string;
  groupVisitId?: string;
  providerIds?: string[];
  sessionStatuses?: string[];
  groupType?: string;
  therapyCategory?: string;
  locationIds?: string[];
  patientId?: string;
  searchKey?: string;
  sortBy?: string;
  sortDirection?: string;
  enabled?: boolean;
};

/**
 * Scheduled group sessions across groups — the group side of the
 * appointments list.
 */
export const useGroupSessions = ({
  pagination,
  fromDate,
  toDate,
  groupVisitId,
  providerIds,
  sessionStatuses,
  groupType,
  therapyCategory,
  locationIds,
  patientId,
  searchKey,
  sortBy,
  sortDirection,
  enabled = true,
}: Params): useBaseContent<GroupVisitSession> => {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey,
    prevPage,
  });
  const pageSize = getPageSize(searchKey, pagination);

  return useQuery({
    queryKey: [
      GROUP_SESSION_LIST_QUERY_KEY,
      pageIndex,
      pageSize,
      fromDate,
      toDate,
      groupVisitId,
      providerIds,
      sessionStatuses,
      groupType,
      therapyCategory,
      locationIds,
      patientId,
      searchKey,
      sortBy,
      sortDirection,
    ],
    enabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      new GroupVisitSessionControllerApi(getConfigObject())
        .getAllSessions(
          pageIndex, // page
          pageSize, // size
          sortBy, // sortBy
          sortDirection, // sortDirection
          fromDate, // fromDate
          toDate, // toDate
          groupVisitId, // groupVisitId
          providerIds?.length ? providerIds : undefined, // providerId
          sessionStatuses?.length ? sessionStatuses : undefined, // sessionStatus
          groupType, // groupType
          therapyCategory, // therapyCategory
          locationIds?.length ? locationIds : undefined, // locationId
          patientId, // patientId
          searchKey ? searchKey : undefined, // searchString
          getXtenantId() // xTENANTID
        )
        .then((res) => res?.data),
  });
};
